import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { addDays, friendlyDay, monthDay } from '../../shared/dates';
import { formatWeight } from '../../shared/units';
import { useDays, useDeleteWeight, useSettings, useWeights } from '../api/hooks';
import { InsightCard, RecapCard } from '../components/AICards';
import { BarChart, WeightChart } from '../components/charts';
import { amount, defFor, directionWord, unitLabel } from '../lib/format';
import { useUI } from '../lib/ui';

const RANGES = [
  { id: '30', label: '1M' },
  { id: '90', label: '3M' },
  { id: '180', label: '6M' },
  { id: 'all', label: 'All' },
] as const;

function WeightTab() {
  const { today, openWeighIn, toast } = useUI();
  const [range, setRange] = useState<string>('90');
  const weights = useWeights(range, today);
  const settings = useSettings();
  const remove = useDeleteWeight();
  const unit = weights.data?.unit ?? settings.data?.profile.weightUnit ?? 'lb';
  const aiEnabled = settings.data?.ai.enabled ?? false;
  const s = weights.data?.summary;
  const trendByDate = useMemo(() => new Map(weights.data?.trend.map((t) => [t.date, t.kg])), [weights.data]);

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="stats">
        <div className="card stat">
          <span className="stat__label">Trend weight</span>
          <span className="stat__value num">{s?.trendKg != null ? formatWeight(s.trendKg, unit) : '—'}</span>
          <span className="stat__sub num">
            {s?.rate ? `${formatWeight(s.rate.kgPerWeek, unit, { signed: true, decimals: 2 })} / week` : 'Need ~10 days of weigh-ins'}
          </span>
        </div>
        <div className="card stat">
          <span className="stat__label">To goal</span>
          <span className="stat__value num">{s?.toGoKg != null ? formatWeight(Math.abs(s.toGoKg), unit) : '—'}</span>
          <span className="stat__sub">{s?.percentComplete != null ? `${s.percentComplete}% of the way` : 'Set a goal in You'}</span>
        </div>
        <div className="card stat">
          <span className="stat__label">On pace for</span>
          <span className="stat__value">{s?.projection ? monthDay(s.projection.date) : '—'}</span>
          <span className="stat__sub">
            {s?.projection
              ? s.projection.latest
                ? `${monthDay(s.projection.earliest)} – ${monthDay(s.projection.latest)}`
                : `${monthDay(s.projection.earliest)} or later`
              : 'Not trending toward goal yet'}
          </span>
        </div>
      </div>

      {s?.flags.rapidLoss && (
        <div className="banner banner--warn" role="alert">
          <AlertTriangle size={18} />
          <span className="small">
            <b>You're losing faster than 1% of your weight per week.</b> That pace can cost muscle and energy. Consider eating a bit more, and check in
            with a clinician if it continues.
          </span>
        </div>
      )}

      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">Weight</h2>
          <div className="seg" role="radiogroup" aria-label="Range">
            {RANGES.map((r) => (
              <button key={r.id} role="radio" aria-checked={range === r.id} className={`seg__btn ${range === r.id ? 'is-active' : ''}`} onClick={() => setRange(r.id)}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {weights.data ? (
          <WeightChart
            points={weights.data.entries}
            trend={weights.data.trend}
            goalKg={s?.goalKg ?? null}
            unit={unit}
            today={today}
            forecastRate={s?.rate && s.projection ? s.rate.kgPerWeek : null}
          />
        ) : (
          <div className="skeleton" style={{ height: 240 }} />
        )}
        <div className="legend" style={{ marginTop: 10 }}>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--ink)' }} />
            Trend
          </span>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--faint)', height: 6, width: 6, borderRadius: 3 }} />
            Weigh-ins
          </span>
          <span>
            <span className="legend__swatch" style={{ background: 'var(--fiber)' }} />
            Goal
          </span>
          {s?.projection && (
            <span>
              <span className="legend__swatch" style={{ background: 'var(--ai)' }} />
              Forecast
            </span>
          )}
        </div>
      </section>

      <InsightCard aiEnabled={aiEnabled} />
      <RecapCard aiEnabled={aiEnabled} unit={unit} />

      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">Weigh-ins</h2>
          <button className="btn btn--accent btn--sm" onClick={() => openWeighIn()}>
            <Plus size={16} /> Weigh in
          </button>
        </div>
        <div className="list">
          {[...(weights.data?.entries ?? [])]
            .reverse()
            .slice(0, 30)
            .map((w) => {
              const trend = trendByDate.get(w.date);
              const delta = trend !== undefined ? w.kg - trend : null;
              return (
                <div className="list-item" key={w.id}>
                  <div className="grow">
                    <div style={{ fontWeight: 600 }}>{friendlyDay(w.date, today)}</div>
                    {delta !== null && Math.abs(delta) >= 0.05 && (
                      <div className="tiny muted num">{formatWeight(delta, unit, { signed: true })} vs trend</div>
                    )}
                  </div>
                  <span className="num" style={{ fontWeight: 650 }}>
                    {formatWeight(w.kg, unit)}
                  </span>
                  <button className="btn btn--quiet btn--icon btn--sm" aria-label={`Edit weigh-in from ${friendlyDay(w.date, today)}`} onClick={() => openWeighIn({ date: w.date })}>
                    <Pencil size={15} />
                  </button>
                  <button
                    className="btn btn--quiet btn--icon btn--sm"
                    aria-label={`Delete weigh-in from ${friendlyDay(w.date, today)}`}
                    onClick={() => remove.mutate(w.id, { onSuccess: () => toast('Weigh-in deleted'), onError: (e) => toast(e.message, { tone: 'error' }) })}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
          {weights.data && weights.data.entries.length === 0 && <div className="empty small">No weigh-ins in this range.</div>}
        </div>
      </section>
    </div>
  );
}

function NutritionTab() {
  const { today } = useUI();
  const [span, setSpan] = useState<7 | 30>(7);
  const settings = useSettings();
  const from = addDays(today, -(span - 1));
  const days = useDays(from, today);
  const tracked = (settings.data?.tracking ?? []).filter((t) => t.tracked);
  const [key, setKey] = useState('kcal');
  const defs = useMemo(() => new Map((settings.data?.nutrients ?? []).map((n) => [n.key, n])), [settings.data]);
  const def = defFor(key, defs);
  const goal = tracked.find((t) => t.key === key);
  const goalAmount = goal?.target ?? null;
  const goalDirection = goal?.direction ?? 'target';
  const series = (days.data ?? []).map((d) => ({ date: d.date, amount: d.entryCount ? (d.totals[key] ?? 0) : null }));
  const logged = series.filter((d) => d.amount !== null);
  const avg = logged.length ? logged.reduce((s, d) => s + (d.amount ?? 0), 0) / logged.length : undefined;
  const hits =
    goalAmount !== null && goalAmount > 0
      ? logged.filter((d) => {
          const v = d.amount ?? 0;
          if (goalDirection === 'min') return v >= goalAmount;
          if (goalDirection === 'max') return v <= goalAmount;
          return Math.abs(v - goalAmount) <= goalAmount * 0.1;
        }).length
      : null;
  const partialDays = (days.data ?? []).filter((d) => d.entryCount > 0 && (d.known[key] ?? 0) < d.entryCount).length;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="row between wrap">
        <div className="scroll-x" style={{ margin: 0, padding: 0, flex: '1 1 auto' }} role="tablist" aria-label="Nutrient">
          {tracked.map((t) => (
            <button key={t.key} role="tab" aria-selected={key === t.key} className={`chip ${key === t.key ? 'is-active' : ''}`} onClick={() => setKey(t.key)}>
              {defFor(t.key, defs).name}
            </button>
          ))}
        </div>
        <div className="seg" role="radiogroup" aria-label="Range">
          {([7, 30] as const).map((n) => (
            <button key={n} role="radio" aria-checked={span === n} className={`seg__btn ${span === n ? 'is-active' : ''}`} onClick={() => setSpan(n)}>
              {n}D
            </button>
          ))}
        </div>
      </div>
      <div className="stats">
        <div className="card stat">
          <span className="stat__label">Daily average</span>
          <span className="stat__value num">
            {amount(avg, def)} <span style={{ fontSize: 15 }}>{unitLabel(def)}</span>
          </span>
          <span className="stat__sub">
            {goalAmount !== null ? `${directionWord(goalDirection)} ${amount(goalAmount, def)} ${unitLabel(def)}` : 'No target'}
          </span>
        </div>
        <div className="card stat">
          <span className="stat__label">Days logged</span>
          <span className="stat__value num">
            {logged.length}/{span}
          </span>
          <span className="stat__sub">Averages use logged days only</span>
        </div>
        <div className="card stat">
          <span className="stat__label">On target</span>
          <span className="stat__value num">{hits === null ? '—' : `${hits}/${logged.length}`}</span>
          <span className="stat__sub">logged days</span>
        </div>
      </div>
      <section className="card card--pad">
        <div className="card__head">
          <h2 className="card__title">{def.name}</h2>
          <span className="tiny muted">dashed line = target</span>
        </div>
        {days.data ? (
          <BarChart days={series} target={goalAmount} direction={goalDirection} unit={unitLabel(def)} label={def.name} />
        ) : (
          <div className="skeleton" style={{ height: 200 }} />
        )}
        {partialDays > 0 && key !== 'kcal' && (
          <p className="tiny muted" style={{ marginTop: 8 }}>
            {partialDays} {partialDays === 1 ? 'day has' : 'days have'} foods without {def.name.toLowerCase()} data, so those totals are minimums.
          </p>
        )}
      </section>
    </div>
  );
}

export function ProgressPage() {
  const [tab, setTab] = useState<'weight' | 'nutrition'>('weight');
  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Progress</h1>
          <p className="page__sub">Trends beat single days.</p>
        </div>
      </div>
      <div className="seg seg--block" role="tablist" aria-label="Progress view" style={{ maxWidth: 360 }}>
        {(['weight', 'nutrition'] as const).map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`seg__btn ${tab === t ? 'is-active' : ''}`} onClick={() => setTab(t)}>
            {t === 'weight' ? 'Weight' : 'Nutrition'}
          </button>
        ))}
      </div>
      {tab === 'weight' ? <WeightTab /> : <NutritionTab />}
    </div>
  );
}
